/**
 * Cart Upsell Enhancement Script
 * =================================================
 * Loaded globally and uses event delegation to survive cart drawer DOM replacements.
 */
(function(){
  'use strict';

  // Namespace to prevent conflicts
  window.CartUpsells = window.CartUpsells || {};

  // Prevent multiple initializations
  if (window.CartUpsells.initialized) return;
  window.CartUpsells.initialized = true;

  /* ==========================================================================
     Helper Functions
     ========================================================================== */

  const $ = (sel, root=document) => root.querySelector(sel);
  const $$ = (sel, root=document) => Array.from(root.querySelectorAll(sel));

  const getItemsSectionId = () =>
    $('cart-items-component[data-section-id]')?.dataset.sectionId || null;

  const getSectionKeys = () => {
    const id = getItemsSectionId();
    const list = [];
    if (id) list.push(id);
    list.push('cart-icon-bubble');
    return list;
  };

  const openDrawer = () => {
    const el = document.querySelector('cart-drawer-component');
    if (el && typeof el.open === 'function') el.open();
  };

  const dispatchCompatEvents = (sectionsMap) => {
    try {
      document.dispatchEvent(new CustomEvent('theme:cart:update', {
        detail: { data: { sections: sectionsMap }, source: 'upsell' }
      }));
      document.dispatchEvent(new CustomEvent('theme:cart:add', {
        detail: { source: 'upsell' }
      }));
      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: { source: 'upsell' }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:change'));
    } catch(_) {}
  };

  const upgradeCustomEls = (root) => {
    if (window.customElements && typeof customElements.upgrade === 'function') {
      try { customElements.upgrade(root); } catch(_) {}
    }
  };

  const clearCartCache = () => {
    if (window.cartCache) window.cartCache = null;
    if (window.cart) window.cart = null;
    if (window.theme?.cart) window.theme.cart = null;
  };

  // Lock to prevent concurrent cart operations
  let cartOperationInProgress = false;

  /* ==========================================================================
     UI State Management
     ========================================================================== */

  function setBtn(btn, state){
    const map = {
      default: {c:'', k:'add-text'},
      adding: {c:'adding', k:'adding-text'},
      added: {c:'added', k:'added-text'},
      error: {c:'error', k:'add-text'}
    };
    const cfg = map[state] || map.default;
    btn.className = 'cart-upsell-item__add-button ' + cfg.c;

    // Preserve claim-button class if present
    if (btn.classList.contains('claim-button') || btn.getAttribute('data-claim')) {
      btn.classList.add('claim-button');
    }

    btn.querySelectorAll('.add-text, .adding-text, .added-text').forEach(s => {
      s.style.display = s.classList.contains(cfg.k) ? 'inline' : 'none';
    });

    if (state !== 'error' && btn.title) {
      btn.removeAttribute('title');
    }
  }

  /* ==========================================================================
     Cart DOM Updates
     ========================================================================== */

  function replaceCartItemsComponent(sectionHtml){
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');

    const newComp = doc.querySelector('cart-items-component');
    const curComp = document.querySelector('cart-items-component');

    const itemsScroll = document.querySelector('.cart-drawer__items');
    const prevScrollTop = itemsScroll ? itemsScroll.scrollTop : 0;
    const activeId = document.activeElement && document.activeElement.id ? document.activeElement.id : null;

    if (newComp && curComp) {
      curComp.replaceWith(newComp);
      upgradeCustomEls(newComp);
    } else {
      const newItems = doc.querySelector('.cart-drawer__items');
      const curItems = document.querySelector('.cart-drawer__items');
      if (newItems && curItems) curItems.replaceWith(newItems);
    }

    const itemsScrollNew = document.querySelector('.cart-drawer__items');
    if (itemsScrollNew) itemsScrollNew.scrollTop = prevScrollTop;

    if (activeId) {
      const candidate = document.getElementById(activeId);
      if (candidate) candidate.focus({ preventScroll: true });
    }

    ['cart-drawer--empty','is-empty','empty'].forEach(cls =>
      $$('.' + cls).forEach(el => el.classList.remove(cls))
    );
  }

  function replaceCartBubble(html){
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newBubble = doc.querySelector('.cart-bubble');
    if (!newBubble) return;
    $$('.cart-bubble').forEach(b => {
      b.innerHTML = newBubble.innerHTML;
      b.removeAttribute('hidden');
      b.style.display = '';
    });
  }

  /* ==========================================================================
     Cart Operations
     ========================================================================== */

  async function addVariant(variantId){
    // Prevent concurrent cart operations
    if (cartOperationInProgress) {
      throw new Error('Cart operation already in progress. Please wait.');
    }

    cartOperationInProgress = true;

    try {
      const sectionId = getItemsSectionId();
      const sections = getSectionKeys();
      const sectionsParam = Array.isArray(sections) ? sections : sections.join(',');

      const res = await fetch('/cart/add', {
        method:'POST',
        headers:{
          'Content-Type':'application/json',
          'Accept':'application/json',
          'X-Requested-With':'XMLHttpRequest'
        },
        body: JSON.stringify({
          items:[{ id: Number(variantId), quantity: 1 }],
          sections: sectionsParam,
          sections_url: window.location.pathname
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        const errorMsg = errorData?.description || errorData?.message || 'Failed to add item';
        throw new Error(errorMsg);
      }

      const state = await res.json();
      clearCartCache();

      // Update DOM - use morphSection for proper cart updates
      if (state.sections) {
        if (sectionId && state.sections[sectionId]) {
          const morph = window.morphSection || (window.theme && window.theme.morphSection);
          if (typeof morph === 'function') {
            morph(sectionId, state.sections[sectionId]);
          } else {
            // Fallback if morph not available
            replaceCartItemsComponent(state.sections[sectionId]);
          }
        }

        if (state.sections['cart-icon-bubble']) {
          replaceCartBubble(state.sections['cart-icon-bubble']);
        }

        dispatchCompatEvents(state.sections);
      } else {
        // Fallback: fetch sections manually
        const qs = new URLSearchParams({
          sections: sections.join(','),
          sections_url: window.location.pathname
        });
        const secRes = await fetch('/?' + qs.toString(), {
          headers: { 'Accept': 'application/json' }
        });

        if (!secRes.ok) {
          throw new Error('Failed to fetch updated cart sections');
        }

        const secMap = await secRes.json();
        clearCartCache();

        if (sectionId && secMap[sectionId]) {
          replaceCartItemsComponent(secMap[sectionId]);
        }

        if (secMap['cart-icon-bubble']) {
          replaceCartBubble(secMap['cart-icon-bubble']);
        }

        dispatchCompatEvents(secMap);
      }

      openDrawer();
    } finally {
      // Always release the lock
      cartOperationInProgress = false;
    }
  }

  /* ==========================================================================
     Event Handlers (Using Event Delegation)
     ========================================================================== */

  function onUpsellClick(e){
    // Event delegation: check if click was on upsell button
    const btn = e.target.closest('.cart-upsell-item__add-button');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    const variantId = btn.dataset.variantId;
    const tile = btn.closest('.cart-upsell-item');

    if (btn.dataset.locked === '1') return;
    btn.dataset.locked = '1';

    setBtn(btn,'adding');

    addVariant(variantId)
      .then(() => {
        setBtn(btn,'added');

        setTimeout(() => {
          if (tile) {
            tile.classList.add('removing');
            setTimeout(() => {
              tile.remove();

              if (!document.querySelector('.cart-upsell-item')) {
                const c = document.querySelector('.cart-upsells');
                if (c) c.style.display = 'none';
              }
            }, 280);
          }
        }, 500);
      })
      .catch(err => {
        console.error('Cart upsell error:', err);
        setBtn(btn,'error');

        const errorMsg = err.message || 'Failed to add item. Please try again.';
        btn.setAttribute('title', errorMsg);

        if (window.theme?.showNotification) {
          window.theme.showNotification(errorMsg, 'error');
        }
      })
      .finally(() => {
        setTimeout(() => {
          btn.dataset.locked = '0';
          setBtn(btn,'default');
        }, 2000);
      });
  }

  function updateUpsellsVisibility() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        // Count total mitts and check for FREE face mitt
        let mittCount = 0;
        let hasFreeFaceMitt = false;

        cart.items.forEach(item => {
          const title = (item.title || item.product_title || '').toLowerCase();

          // Count mitts
          if (title.includes('mitt')) {
            mittCount += item.quantity;
          }

          // Check for FREE face mitt (price = 0)
          if (title.includes('face') && title.includes('mitt')) {
            if (item.final_price === 0 || item.price === 0) {
              hasFreeFaceMitt = true;
            }
          }
        });

        const cartProductIds = cart.items.map(item => item.product_id);

        document.querySelectorAll('.cart-upsell-item').forEach(upsell => {
          const productId = parseInt(upsell.dataset.productId);
          const productHandle = upsell.dataset.productHandle || '';

          // Special logic for face mitt free offer
          if (productHandle === 'face-mitt' && mittCount >= 3) {
            // Only remove if there's already a FREE face mitt in cart
            if (hasFreeFaceMitt) {
              upsell.remove();
            }
            // Otherwise keep it visible (don't remove)
          } else {
            // Normal logic: remove if product is in cart
            if (cartProductIds.includes(productId)) {
              upsell.remove();
            }
          }
        });

        const remaining = document.querySelectorAll('.cart-upsell-item');
        if (remaining.length === 0) {
          const container = document.querySelector('.cart-upsells');
          if (container) container.style.display = 'none';
        }
      })
      .catch(console.error);
  }

  /* ==========================================================================
     Initialization
     ========================================================================== */

  function init() {
    // Use event delegation on document - survives DOM replacements
    document.addEventListener('click', onUpsellClick);

    // Listen for cart update events with debouncing to prevent race conditions
    let updateTimeout;
    document.addEventListener('cart:update', () => {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(updateUpsellsVisibility, 300);
    });
    document.addEventListener('cart:refresh', () => {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(updateUpsellsVisibility, 300);
    });

    // Listen for Shopify theme editor section events
    document.addEventListener('shopify:section:load', updateUpsellsVisibility);
  }

  // Initialize when DOM is ready
  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // Expose for manual re-initialization if needed
  window.CartUpsells.reinit = init;
  window.CartUpsells.updateVisibility = updateUpsellsVisibility;

})();
